"use client";

import logger from "@/utils/logger";
// components/auth/EnhancedAuthGuard.tsx

import React, { useState, useEffect } from "react";
import { Button, Spinner } from "react-bootstrap";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import Footer from "@/components/layout/Footer";
import ErrorPage from "@/components/errors/ErrorPage";
import Error403Icon from "@/components/errors/icons/Error403Icon";
import { useTheme } from "next-themes";
import {
    computeAuthRequiredDelay,
    hasRecoverableAuthHints,
    isStandaloneDisplayRoute,
    readAuthHintSnapshot,
} from "./authRecovery";
import {
    ACCOUNT_INACTIVE_REASON,
    readStoredAccountAccessState,
} from "@/services/accountAccessState";
import { useAuth } from "@/context/auth-context";
import { useRecoverableProtectedAuth } from "@/hooks/useRecoverableProtectedAuth";

interface AuthGuardProps {
    children: React.ReactNode;
    fallback?: React.ReactNode;
    requireProfile?: boolean;
    loadingComponent?: React.ReactNode;
    redirectTo?: string;
    showContainer?: boolean;
    minHeight?: string;
    waitTime?: number;
    // 🔥 NEW: User type restrictions
    allowedUserTypes?: ("internal" | "external")[];
    userTypeRestrictedMessage?: string;
}

const EnhancedAuthGuard: React.FC<AuthGuardProps> = ({
    children,
    fallback,
    requireProfile = true,
    loadingComponent,
    redirectTo = "/",
    minHeight = "100vh",
    waitTime = 3000, // 🔥 FIX: Increased from 1500ms to 3000ms to allow permissions to load
    // 🔥 NEW: User type props
    allowedUserTypes = ["internal", "external"],
    userTypeRestrictedMessage,
}) => {
    const {
        isAuthenticated,
        isLoading,
        isProcessingRedirect,
        userProfile,
        externalUserProfile,
        loginType,
        authError,
    } = useRecoverableProtectedAuth();
    const router = useRouter();
    const pathname = usePathname() ?? "";
    const { resolvedTheme } = useTheme();
    const isStandalonePath = pathname.startsWith("/display/");

    // State to handle waiting period and global logout detection
    const [showAuthRequired, setShowAuthRequired] = useState(false);
    const [isGlobalLogoutDetected, setIsGlobalLogoutDetected] = useState(false);
    const [isStandalone, setIsStandalone] = useState(isStandalonePath);
    // 🔥 NEW: Track if user was recently authenticated to prevent navigation flashing
    const [wasRecentlyAuthenticated, setWasRecentlyAuthenticated] =
        useState(false);
    const [hasRenderedAuthorizedContent, setHasRenderedAuthorizedContent] =
        useState(false);
    const [isThemeReady, setIsThemeReady] = useState(false);

    const authHintSnapshot = React.useMemo(
        () =>
            readAuthHintSnapshot(
                typeof window === "undefined" ? null : window.localStorage,
                wasRecentlyAuthenticated
            ),
        [wasRecentlyAuthenticated]
    );
    const hasRecoverableHints = React.useMemo(
        () => hasRecoverableAuthHints(authHintSnapshot),
        [authHintSnapshot]
    );
    useEffect(() => {
        setIsThemeReady(true);
    }, []);

    useEffect(() => {
        // Keep standalone behavior route-driven only to avoid stale body-class
        // toggles causing protected routes to skip auth/session handling.
        setIsStandalone(isStandalonePath);
    }, [isStandalonePath]);

    // Determine current profile based on login type
    const currentProfile = React.useMemo(() => {
        return loginType === "msal" ? userProfile : externalUserProfile;
    }, [loginType, userProfile, externalUserProfile]);

    // 🔥 NEW: Determine current user type and check if allowed
    const { currentUserType, isUserTypeAllowed, isUserTypeResolved } =
        React.useMemo(() => {
            if (!loginType) {
                return {
                    currentUserType: null,
                    isUserTypeAllowed: true,
                    isUserTypeResolved: false,
                };
            }

            const userType = loginType === "msal" ? "internal" : "external";
            const isAllowed = allowedUserTypes.includes(userType);

            logger.info("🔒 EnhancedAuthGuard User Type Check:", {
                userType,
                allowedUserTypes,
                isAllowed,
                userName: currentProfile?.displayName,
            });

            return {
                currentUserType: userType,
                isUserTypeAllowed: isAllowed,
                isUserTypeResolved: true,
            };
        }, [loginType, allowedUserTypes, currentProfile]);

    // Check if user is authorized (both authenticated and user type allowed)
    const isAuthorized = React.useMemo(() => {
        return (
            isAuthenticated &&
            (!requireProfile || currentProfile) &&
            isUserTypeAllowed
        );
    }, [isAuthenticated, requireProfile, currentProfile, isUserTypeAllowed]);

    const isRecoveringAuth =
        !isAuthorized &&
        !isAuthenticated &&
        !isGlobalLogoutDetected &&
        hasRecoverableHints;

    // Track when user becomes authenticated to prevent flashing during navigation
    useEffect(() => {
        if (isAuthorized) {
            setWasRecentlyAuthenticated(true);
            setHasRenderedAuthorizedContent(true);
            return;
        }

        if (wasRecentlyAuthenticated) {
            const timer = setTimeout(
                () => setWasRecentlyAuthenticated(false),
                2000
            );
            return () => clearTimeout(timer);
        }
    }, [isAuthorized, wasRecentlyAuthenticated]);

    const shouldPreserveAuthorizedContent =
        !isStandalone &&
        !isStandaloneDisplayRoute(pathname) &&
        !isGlobalLogoutDetected &&
        !isLoading &&
        !isProcessingRedirect &&
        hasRecoverableHints &&
        hasRenderedAuthorizedContent &&
        isUserTypeAllowed;

    // 🔥 NEW: Listen for global logout events
    useEffect(() => {
        if (isStandalone) {
            return;
        }
        const handleGlobalLogout = (event: StorageEvent) => {
            if (event.key === "app_logout_event" && event.newValue) {
                logger.info("🔔 Global logout detected in AuthGuard");
                setIsGlobalLogoutDetected(true);

                let redirectPath = redirectTo;
                try {
                    const parsedEvent = JSON.parse(event.newValue) as {
                        reason?: string;
                        redirectPath?: string;
                    };
                    if (
                        typeof parsedEvent.redirectPath === "string" &&
                        parsedEvent.redirectPath.trim()
                    ) {
                        redirectPath = parsedEvent.redirectPath;
                    } else if (parsedEvent.reason === ACCOUNT_INACTIVE_REASON) {
                        redirectPath = "/403";
                    }
                } catch {
                    // Ignore malformed cross-tab logout payloads.
                }

                // Immediate redirect on global logout
                setTimeout(() => {
                    window.location.replace(redirectPath);
                }, 100);
            }
        };

        window.addEventListener("storage", handleGlobalLogout);

        return () => {
            window.removeEventListener("storage", handleGlobalLogout);
        };
    }, [redirectTo, isStandalone]);

    useEffect(() => {
        if (isStandalone || pathname === "/403" || isAuthenticated) {
            return;
        }

        const accessState = readStoredAccountAccessState();
        if (accessState?.reason === ACCOUNT_INACTIVE_REASON) {
            window.location.replace("/403");
        }
    }, [isAuthenticated, isStandalone, pathname]);

    // 🔥 NEW: Check for session integrity on mount and focus
    useEffect(() => {
        if (isStandalone) {
            return;
        }
        const ensureAuthStateMarker = () => {
            try {
                localStorage.setItem(
                    "app_auth_state",
                    JSON.stringify({
                        timestamp: Date.now(),
                        authenticated: true,
                        source: "auth-guard-repair",
                    })
                );
            } catch (error) {
                logger.info("🔔 Failed to repair auth state marker:", error);
            }
        };

        const checkSessionIntegrity = () => {
            if (!isAuthenticated || isLoading || isProcessingRedirect) {
                return;
            }

            const authState = localStorage.getItem("app_auth_state");
            if (!authState) {
                logger.info(
                    "🔔 Session marker missing while authenticated; restoring marker instead of forcing logout."
                );
                ensureAuthStateMarker();
            }
        };

        // Check on mount
        checkSessionIntegrity();

        // Check on window focus
        const handleFocus = () => {
            checkSessionIntegrity();
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                checkSessionIntegrity();
            }
        };

        window.addEventListener("focus", handleFocus);
        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            window.removeEventListener("focus", handleFocus);
            document.removeEventListener(
                "visibilitychange",
                handleVisibilityChange
            );
        };
    }, [isAuthenticated, isLoading, isProcessingRedirect, isStandalone]);

    // Handle waiting period for unauthenticated users
    useEffect(() => {
        if (isStandalone || isStandalonePath) {
            setShowAuthRequired(false);
            return;
        }
        const isProfilePending =
            isAuthenticated && requireProfile && !currentProfile;
        const isUserTypePending = isAuthenticated && !isUserTypeResolved;
        // If global logout detected, don't show auth required
        if (isGlobalLogoutDetected) {
            return;
        }

        // If still loading or user is authorized, don't start timer
        if (
            isLoading ||
            isProcessingRedirect ||
            isAuthorized ||
            shouldPreserveAuthorizedContent ||
            isProfilePending ||
            isUserTypePending
        ) {
            setShowAuthRequired(false);
            return;
        }

        const extendedWaitTime = computeAuthRequiredDelay(
            waitTime,
            authHintSnapshot
        );

        logger.info("🔒 AuthGuard: Starting auth required timer", {
            isLoading,
            isProcessingRedirect,
            isAuthorized,
            isAuthenticated,
            currentProfile: !!currentProfile,
            hasRecoverableHints,
            authHintSnapshot,
            waitTime: extendedWaitTime,
            requireProfile,
            isProfilePending,
        });

        // Only start waiting timer if user is definitely not authenticated
        const timer = setTimeout(() => {
            // Double-check before showing auth required
            if (
                !isLoading &&
                !isProcessingRedirect &&
                !isAuthorized &&
                !isGlobalLogoutDetected
            ) {
                logger.info("🔒 AuthGuard: Showing auth required after wait", {
                    isAuthenticated,
                    currentProfile: !!currentProfile,
                    isUserTypeAllowed,
                });
                setShowAuthRequired(true);
            }
        }, extendedWaitTime);

        return () => clearTimeout(timer);
    }, [
        isLoading,
        isProcessingRedirect,
        isAuthorized,
        shouldPreserveAuthorizedContent,
        waitTime,
        isGlobalLogoutDetected,
        hasRecoverableHints,
        authHintSnapshot,
        currentProfile,
        isAuthenticated,
        requireProfile,
        isUserTypeAllowed,
        isUserTypeResolved,
        isStandalone,
        isStandalonePath,
    ]);

    // Handle navigation to login/redirect page
    const handleRedirect = React.useCallback(() => {
        if (typeof window !== "undefined") {
            if (redirectTo.startsWith("http")) {
                window.location.href = redirectTo;
            } else {
                router.push(redirectTo);
            }
        }
    }, [redirectTo, router]);

    // 🔥 ENHANCED: Loading component with global logout awareness
    const LoadingComponent = React.useMemo(() => {
        if (loadingComponent) {
            return loadingComponent;
        }

        const loadingText = isGlobalLogoutDetected
            ? "Logging out..."
            : isLoading || isProcessingRedirect || isRecoveringAuth
              ? "Checking authentication..."
              : "Loading page...";

        return (
            <div
                className="auth-guard-screen d-flex flex-column justify-content-center align-items-center"
                style={{
                    minHeight: minHeight,
                    backgroundColor: "var(--theme-bg)",
                    fontFamily:
                        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                }}
            >
                <div className="text-center">
                    <Spinner
                        animation="border"
                        variant={isGlobalLogoutDetected ? "warning" : "primary"}
                        style={{ width: "2rem", height: "2rem" }}
                    />
                    <div
                        className="mt-3"
                        style={{
                            color: "var(--theme-text-muted)",
                            fontSize: "0.9rem",
                        }}
                    >
                        {loadingText}
                    </div>
                </div>
            </div>
        );
    }, [
        loadingComponent,
        isLoading,
        isProcessingRedirect,
        isRecoveringAuth,
        isGlobalLogoutDetected,
        minHeight,
    ]);

    // 🔥 NEW: User type restricted UI - Updated Design
    const UserTypeRestrictedUI = React.useMemo(() => {
        const defaultMessage =
            currentUserType === "external"
                ? "This page is only available to internal users. External users have access to a limited feature set."
                : "This page is only available to external users.";

        const redirectHref =
            currentUserType === "external"
                ? "/externalLoginDashboard"
                : "/dashboard";

        const isDark = isThemeReady && resolvedTheme === "dark";
        const accent = isDark ? "#ed6a6a" : "#dc2625";
        const background = isDark ? "#181a1b" : "#fef2f2";
        const messageColor = isDark ? "#e0e0e0" : "#000000";

        return (
            <ErrorPage
                code={403}
                background={background}
                accent={accent}
                title="Access Restricted"
                message={
                    <>
                        <div style={{ marginBottom: 8 }}>
                            {userTypeRestrictedMessage || defaultMessage}
                        </div>
                        <div>
                            Logged in as{" "}
                            <strong>{currentProfile?.displayName}</strong> (
                            {currentUserType} user)
                        </div>
                    </>
                }
                primaryAction={{
                    kind: "link",
                    label:
                        currentUserType === "external"
                            ? "Go to External Dashboard"
                            : "Go to Main Dashboard",
                    href: redirectHref,
                }}
                secondaryAction={{ kind: "back", label: "Go Back" }}
                icon={<Error403Icon />}
                messageColor={messageColor}
            />
        );
    }, [
        currentProfile?.displayName,
        currentUserType,
        userTypeRestrictedMessage,
        isThemeReady,
        resolvedTheme,
    ]);

    const AuthRequiredUI = React.useMemo(() => {
        const isDark = isThemeReady && resolvedTheme === "dark";
        const iconSrc = isDark ? "/errors/403_darkmode.svg" : "/errors/403.svg";
        const accent = isDark ? "#ed6a6a" : "#dc2625";
        const background = isDark ? "#181a1b" : "#fef2f2";
        const message = isGlobalLogoutDetected
            ? "Your session has ended. Please log in again to continue."
            : "Please log in to access this page and view your account details.";

        return (
            <ErrorPage
                code={403}
                background={background}
                accent={accent}
                title={
                    isGlobalLogoutDetected
                        ? "Session Ended"
                        : "Authentication Required"
                }
                message={
                    <>
                        <div style={{ marginBottom: 8 }}>{message}</div>
                        {authError && !isGlobalLogoutDetected && (
                            <div>{authError}</div>
                        )}
                    </>
                }
                primaryAction={{
                    kind: "retry",
                    label: isGlobalLogoutDetected
                        ? "Login Again"
                        : "Go to Login",
                    onRetry: handleRedirect,
                }}
                secondaryAction={{ kind: "back", label: "Go Back" }}
                icon={
                    <Image
                        src={iconSrc}
                        alt=""
                        aria-hidden="true"
                        width={91}
                        height={91}
                        style={{ display: "block" }}
                    />
                }
                messageColor={isDark ? "#e0e0e0" : "#000000"}
            />
        );
    }, [
        authError,
        handleRedirect,
        isGlobalLogoutDetected,
        isThemeReady,
        resolvedTheme,
    ]);

    if (isStandalone || isStandalonePath) {
        return <>{children}</>;
    }

    // Show loading if global logout is detected
    if (isGlobalLogoutDetected) {
        return <>{LoadingComponent}</>;
    }

    // Show content immediately if user is authorized
    if (isAuthorized) {
        return <>{children}</>;
    }

    if (shouldPreserveAuthorizedContent) {
        return <>{children}</>;
    }

    // Wait for login type resolution before enforcing user type restrictions
    if (isAuthenticated && !isUserTypeResolved) {
        return <>{LoadingComponent}</>;
    }

    // 🔥 NEW: Show user type restriction if authenticated but wrong user type
    if (isAuthenticated && currentProfile && !isUserTypeAllowed) {
        return <>{UserTypeRestrictedUI}</>;
    }

    // Show loading while auth context is loading or while recoverable auth hints
    // are being restored, but never block the explicit auth-required fallback.
    if (
        !showAuthRequired &&
        (isLoading || isProcessingRedirect || isRecoveringAuth)
    ) {
        return <>{LoadingComponent}</>;
    }

    // Show auth required only after waiting period
    if (showAuthRequired) {
        if (fallback) {
            return <>{fallback}</>;
        }
        return <>{AuthRequiredUI}</>;
    }

    // Show loading while timer is running.
    return <>{LoadingComponent}</>;
};

export default EnhancedAuthGuard;

// Export additional types for convenience
export type { AuthGuardProps };

// 🔥 ENHANCED: Updated guard variants with user type support
export const SimpleAuthGuard: React.FC<{
    children: React.ReactNode;
    allowedUserTypes?: ("internal" | "external")[];
}> = ({ children, allowedUserTypes }) => {
    return (
        <EnhancedAuthGuard
            requireProfile={true}
            showContainer={true}
            waitTime={400} // 🔥 REDUCED: From 800ms to 400ms for faster navigation
            allowedUserTypes={allowedUserTypes}
        >
            {children}
        </EnhancedAuthGuard>
    );
};

export const FastAuthGuard: React.FC<{
    children: React.ReactNode;
    allowedUserTypes?: ("internal" | "external")[];
}> = ({ children, allowedUserTypes }) => {
    return (
        <EnhancedAuthGuard
            requireProfile={true}
            showContainer={true}
            waitTime={200}
            allowedUserTypes={allowedUserTypes}
        >
            {children}
        </EnhancedAuthGuard>
    );
};

export const InstantAuthGuard: React.FC<{
    children: React.ReactNode;
    allowedUserTypes?: ("internal" | "external")[];
}> = ({ children, allowedUserTypes }) => {
    return (
        <EnhancedAuthGuard
            requireProfile={true}
            showContainer={true}
            waitTime={50}
            allowedUserTypes={allowedUserTypes}
        >
            {children}
        </EnhancedAuthGuard>
    );
};

export const AdminAuthGuard: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { userProfile, externalUserProfile, loginType } = useAuth();
    const currentProfile =
        loginType === "msal" ? userProfile : externalUserProfile;

    const hasAdminRole = React.useMemo(() => {
        if (!currentProfile) return false;
        const roles = currentProfile.roles || [];
        return roles.includes("Admin") || roles.includes("Administrator");
    }, [currentProfile]);

    const adminFallback = (
        <div
            className="d-flex flex-column justify-content-between align-items-center"
            style={{
                minHeight: "100vh",
                backgroundColor: "var(--theme-bg)",
                fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                padding: "2rem 1rem",
            }}
        >
            {/* Main Content */}
            <div className="auth-guard-screen flex-grow-1 d-flex flex-column justify-content-center align-items-center">
                <div className="text-center" style={{ maxWidth: "400px" }}>
                    {/* Warning Icon */}
                    <div className="mb-4">
                        <svg
                            width="60"
                            height="60"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#dc3545"
                            strokeWidth="1.5"
                            className="mx-auto"
                        >
                            <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                            <line x1="12" y1="9" x2="12" y2="13" />
                            <line x1="12" y1="17" x2="12.01" y2="17" />
                        </svg>
                    </div>

                    {/* Heading */}
                    <h1
                        className="mb-3"
                        style={{
                            color: "#dc3545",
                            fontSize: "1.5rem",
                            fontWeight: "600",
                            margin: "0 0 1rem 0",
                        }}
                    >
                        Admin Access Required
                    </h1>

                    {/* Description */}
                    <p
                        style={{
                            color: "#8a8a8a",
                            fontSize: "0.95rem",
                            lineHeight: "1.5",
                            margin: "0 0 2rem 0",
                        }}
                    >
                        This page requires administrator privileges. Please
                        contact your administrator if you believe you should
                        have access.
                    </p>

                    {/* Action Buttons */}
                    <div className="d-flex gap-3 justify-content-center mb-3">
                        <Button
                            variant="outline-secondary"
                            onClick={() => window.history.back()}
                            style={{
                                borderRadius: "8px",
                                padding: "0.75rem 1.5rem",
                                fontWeight: "500",
                                fontSize: "0.9rem",
                                borderColor: "#dee2e6",
                                color: "#6c757d",
                                backgroundColor: "white",
                                // 🔥 DISABLE HOVER EFFECTS
                                transition: "none",
                                cursor: "pointer",
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = "white";
                                e.currentTarget.style.borderColor = "#dee2e6";
                                e.currentTarget.style.color = "#6c757d";
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = "white";
                                e.currentTarget.style.borderColor = "#dee2e6";
                                e.currentTarget.style.color = "#6c757d";
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => window.history.back()}
                            style={{
                                borderRadius: "8px",
                                padding: "0.75rem 1.5rem",
                                fontWeight: "500",
                                fontSize: "0.9rem",
                                backgroundColor: "#007bff",
                                borderColor: "#007bff",
                                color: "white",
                            }}
                        >
                            Go Back
                        </Button>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div style={{ marginBottom: "-2rem", width: "102%" }}>
                <Footer />
            </div>
        </div>
    );

    return (
        <EnhancedAuthGuard
            requireProfile={true}
            fallback={hasAdminRole ? undefined : adminFallback}
            waitTime={500}
            allowedUserTypes={["internal"]} // 🔥 NEW: Admin is always internal only
        >
            {children}
        </EnhancedAuthGuard>
    );
};

// 🔥 NEW: Convenient guards for specific user types
export const InternalOnlyGuard: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    return (
        <EnhancedAuthGuard
            requireProfile={true}
            allowedUserTypes={["internal"]}
            userTypeRestrictedMessage="This page is only available to internal users. External users have access to a limited set of features."
        >
            {children}
        </EnhancedAuthGuard>
    );
};

export const ExternalOnlyGuard: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    return (
        <EnhancedAuthGuard
            requireProfile={true}
            allowedUserTypes={["external"]}
            userTypeRestrictedMessage="This page is only available to external users."
        >
            {children}
        </EnhancedAuthGuard>
    );
};

// Export hook for checking auth status with global logout awareness
export const useAuthGuardStatus = () => {
    const {
        isAuthenticated,
        isLoading,
        userProfile,
        externalUserProfile,
        loginType,
        authError,
    } = useAuth();

    const [isGlobalLogoutDetected, setIsGlobalLogoutDetected] = useState(false);

    const currentProfile = React.useMemo(() => {
        return loginType === "msal" ? userProfile : externalUserProfile;
    }, [loginType, userProfile, externalUserProfile]);

    const currentUserType = React.useMemo(() => {
        return loginType === "msal" ? "internal" : "external";
    }, [loginType]);

    // Listen for global logout
    useEffect(() => {
        const handleGlobalLogout = (event: StorageEvent) => {
            if (event.key === "app_logout_event" && event.newValue) {
                setIsGlobalLogoutDetected(true);
            }
        };

        window.addEventListener("storage", handleGlobalLogout);
        return () => window.removeEventListener("storage", handleGlobalLogout);
    }, []);

    return {
        isAuthenticated,
        isLoading,
        currentProfile,
        loginType,
        currentUserType,
        authError,
        isAuthorized: isAuthenticated && currentProfile,
        isGlobalLogoutDetected,
    };
};
